import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Building2, Shield, BarChart3, Globe, TrendingUp, Briefcase, Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ENTERPRISE_BENEFITS, ENTERPRISE_LOGOS, ENTERPRISE_CASE_STUDIES, HERO_IMAGES } from "../lib/mockData";

export default function Enterprise() {
  return (
    <div className="surface-cream min-h-screen" data-testid="enterprise-page">
      <Header/>

      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-[#E1E8E2] py-20">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] blob bg-[#0E6E5E]"/>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] blob bg-[#7CC242]"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="chip mb-6"><Building2 size={13}/> Enterprise & B2B Partnerships</div>
            <h1 className="font-display-tight text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#0A2520] leading-[1.02] mb-6">
              IP infrastructure for <br/>
              <span className="text-[#0E6E5E]">100+ filings</span> a year.
            </h1>
            <p className="text-lg text-[#3D4F4A] leading-relaxed max-w-xl mb-8">
              Built for startups, corporates, R&D centres, and institutions filing patents at scale.
              Dedicated success manager, volume rate cards, whitelabel portal, and PCT international coverage — under one MSA.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/contact" className="btn-primary">Book an Enterprise Demo <ArrowRight size={16}/></Link>
              <a href="#calculator" className="btn-outline">See volume savings ↓</a>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#E1E8E2] max-w-md">
              <Stat label="Enterprise clients" value="40+"/>
              <Stat label="Avg. cost saved" value="35%"/>
              <Stat label="Patents under MSA" value="3,800+"/>
            </div>
          </div>

          <div className="relative">
            <img src={HERO_IMAGES.office} alt="" className="rounded-3xl aspect-[4/5] object-cover w-full"/>
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="bg-white py-10 border-b border-[#E1E8E2]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="text-center text-xs uppercase tracking-widest text-[#5A6B66] font-semibold mb-6">Selected enterprise partners</div>
          <div className="flex items-center justify-center gap-10 lg:gap-16 flex-wrap">
            {ENTERPRISE_LOGOS.map((l) => (
              <span key={l} className="font-display font-bold text-lg text-[#3D4F4A] opacity-60 hover:opacity-100 transition">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 mb-12 items-end">
          <div>
            <div className="chip mb-3">What you get</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] leading-tight">Built for organisations <br/>that take IP seriously.</h2>
          </div>
          <p className="text-lg text-[#3D4F4A] leading-relaxed">
            Whether you're a 100-person deeptech startup or a 50,000-person Fortune-India conglomerate, our
            Enterprise tier consolidates your scattered IP vendors into one accountable partner.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ENTERPRISE_BENEFITS.map((b, i) => (
            <div key={b.title} className="card p-6">
              <div className="text-xs font-mono font-bold text-[#7CC242] mb-3">0{i + 1}</div>
              <div className="font-display font-bold text-lg text-[#0A2520] mb-2">{b.title}</div>
              <p className="text-sm text-[#5A6B66] leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CALCULATOR / SAVINGS */}
      <section id="calculator" className="surface-deep py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="chip chip-gold inline-flex mb-3"><Sparkles size={12}/> ROI calculator</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mb-3">Volume pricing. Real savings.</h2>
            <p className="text-white/70">Indicative pricing for organisations filing more than 100 patents annually.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { volume: "100–250", discount: "15%", price: "₹16,200", desc: "filings / year", best: false },
              { volume: "250–500", discount: "25%", price: "₹14,300", desc: "filings / year", best: true },
              { volume: "500+",    discount: "35%", price: "₹12,400", desc: "filings / year", best: false },
            ].map((t) => (
              <div key={t.volume} className={`rounded-2xl p-8 border ${t.best ? "bg-[#7CC242] border-[#7CC242] text-[#0A2520]" : "bg-white/5 border-white/10 text-white"}`}>
                {t.best && <div className="inline-flex px-2.5 py-1 rounded-full bg-[#0A2520] text-[#7CC242] text-[10px] font-bold uppercase tracking-widest mb-3">Best value</div>}
                <div className="font-display font-bold text-xl mb-1">{t.volume}</div>
                <div className={`text-sm mb-6 ${t.best ? "text-[#0A2520]/70" : "text-white/60"}`}>{t.desc}</div>
                <div className="mb-6">
                  <div className="font-display font-extrabold text-5xl">{t.discount}</div>
                  <div className={`text-sm ${t.best ? "text-[#0A2520]/70" : "text-white/60"}`}>off standard rate</div>
                </div>
                <div className="pt-5 border-t border-white/10">
                  <div className={`text-xs uppercase tracking-wider mb-1 ${t.best ? "text-[#0A2520]/70" : "text-white/60"}`}>Effective price</div>
                  <div className="font-display text-2xl font-bold">{t.price}<span className={`text-sm font-normal ml-1 ${t.best ? "text-[#0A2520]/70" : "text-white/60"}`}>/ filing</span></div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/contact" className="btn-accent">Get a custom rate card <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="chip mb-3">Case studies</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520]">Outcomes across industries</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {ENTERPRISE_CASE_STUDIES.map((cs) => (
            <div key={cs.logo} className="card p-7">
              <div className="font-display font-bold text-lg text-[#0E6E5E] mb-5">{cs.logo}</div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="text-xs text-[#5A6B66] uppercase tracking-wider mb-1">Patents filed</div>
                  <div className="font-display font-extrabold text-3xl text-[#0A2520]">{cs.patents}</div>
                </div>
                <div>
                  <div className="text-xs text-[#5A6B66] uppercase tracking-wider mb-1">Cost saved</div>
                  <div className="font-display font-extrabold text-3xl text-[#7CC242]">{cs.savings}</div>
                </div>
              </div>
              <p className="text-sm text-[#3D4F4A] leading-relaxed">{cs.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white py-20 border-y border-[#E1E8E2]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
          <div>
            <div className="chip mb-3">Why Udhyak Enterprise</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-5 leading-tight">One accountable partner. Many disciplines.</h2>
            <p className="text-[#5A6B66] leading-relaxed mb-7">
              Most large organisations work with 5–7 different IP vendors, each siloed by jurisdiction and domain.
              Udhyak Enterprise consolidates that mess into a single MSA with a dedicated success manager and
              real-time dashboards across your entire portfolio.
            </p>
            <div className="space-y-4">
              {[
                { icon: Briefcase, t: "Single-vendor consolidation", d: "Replace 5+ fragmented vendors with one MSA." },
                { icon: BarChart3, t: "Real-time portfolio dashboards", d: "Filings, grants, geos, competitor IP — in one view." },
                { icon: Globe, t: "PCT + national-phase coverage", d: "150+ jurisdictions through trusted local counsel." },
                { icon: Shield, t: "Enterprise-grade security", d: "ISO 27001, SOC 2 Type II, on-premise dataroom option." },
              ].map((b) => (
                <div key={b.t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg surface-sage flex items-center justify-center text-[#0E6E5E] flex-shrink-0">
                    <b.icon size={18}/>
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[#0A2520] mb-0.5">{b.t}</div>
                    <p className="text-sm text-[#5A6B66] leading-relaxed">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-8 lg:p-10 surface-sage border-[#7CC242]/30">
            <div className="chip chip-accent mb-4"><TrendingUp size={12}/> Custom proposal</div>
            <h3 className="font-display font-bold text-2xl text-[#0A2520] mb-2">Ready to talk volumes?</h3>
            <p className="text-[#3D4F4A] mb-6 leading-relaxed">Get a custom rate card, security questionnaire, and reference calls within 24 hours.</p>

            <div className="space-y-3">
              <input placeholder="Company name" className="input bg-white" data-testid="ent-company"/>
              <input placeholder="Your work email" className="input bg-white" data-testid="ent-email"/>
              <select className="input bg-white" data-testid="ent-volume">
                <option>Estimated annual filings</option>
                <option>50–100</option>
                <option>100–250</option>
                <option>250–500</option>
                <option>500+</option>
              </select>
              <textarea rows={3} placeholder="Tell us about your IP needs (optional)" className="input bg-white resize-none" data-testid="ent-notes"/>
            </div>
            <button className="btn-primary w-full justify-center mt-5">Request a custom rate card <ArrowRight size={14}/></button>
            <div className="text-xs text-[#5A6B66] mt-3 text-center">Avg. response time: 4 business hours</div>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display font-extrabold text-3xl text-[#0E6E5E] mb-1">{value}</div>
      <div className="text-xs text-[#5A6B66] uppercase tracking-widest">{label}</div>
    </div>
  );
}
